import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { Athlete } from './testingTypes';
import { useTestingEntryTable, EnabledTestConfig, PostResultBody } from './useTestingEntryTable';
import styles from './TestingEntryTable.module.scss';

interface TestingEntryTableProps {
  categoryTests: ITestDefinition[];
  athletes: Athlete[];
  dateResultsMap: Record<string, ITestResult>;
  historyMap: Record<string, ITestResult[]>;
  selectedDate: string;
  teamId: string;
  catalog: ITestDefinition[];
  postResult: (body: PostResultBody) => Promise<any>;
  deleteResult: (resultId: string) => Promise<any>;
  /** Config team: metriche e unità selezionate per test (sez.2.1/2.2) */
  enabledTests?: EnabledTestConfig[];
}

export default function TestingEntryTable({
  categoryTests,
  athletes,
  dateResultsMap,
  historyMap,
  selectedDate,
  teamId,
  catalog,
  postResult,
  deleteResult,
  enabledTests,
}: TestingEntryTableProps) {
  const [athleteSearch, setAthleteSearch] = useState('');

  const {
    savingCells,
    getMetrics,
    getUnits,
    getUnitLabel,
    getLastResult,
    getCellValue,
    handleCellChange,
    handleCellBlur,
    getUnitCellValue,
    handleUnitCellChange,
    handleUnitCellBlur,
    getMetricValue,
    handleMetricChange,
    handleMetricBlur,
    handleEnterNavigateDown,
  } = useTestingEntryTable({
    selectedDate,
    teamId,
    catalog,
    dateResultsMap,
    postResult,
    deleteResult,
    enabledTests,
  });

  const filteredAthletes = useMemo(() => {
    if (!athleteSearch.trim()) return athletes;
    const searchLower = athleteSearch.toLowerCase();
    return athletes.filter((athlete) =>
      athlete.name.toLowerCase().includes(searchLower)
    );
  }, [athletes, athleteSearch]);

  /**
   * Costruisce le colonne header per un test:
   * - multi-unit → una th per ogni unit (sez.3/6)
   * - altrimenti → una th singola
   */
  const renderTestHeader = (test: ITestDefinition) => {
    const units = getUnits(test);
    if (units.length > 1) {
      return units.map((unit) => (
        <th key={`${test.id}__unit__${unit}`} className={styles.thTestCell}>
          <div className={styles.thTestInner}>
            <p className={styles.thTestName}>{test.name}</p>
            {/* Mostra la label personalizzata se configurata, altrimenti l'unità grezza */}
            <p className={styles.thTestUnit}>{getUnitLabel(test, unit)}</p>
          </div>
        </th>
      ));
    }
    return (
      <th key={test.id} className={styles.thTestCell}>
        <div className={styles.thTestInner}>
          <p className={styles.thTestName}>{test.name}</p>
          {/* Single-unit: applica label personalizzata se presente */}
          <p className={styles.thTestUnit}>{getUnitLabel(test, test.defaultUnit)}</p>
        </div>
      </th>
    );
  };

  /**
   * Cella multi-unit affiancata (sez.3/6):
   * una cella per unit, ciascuna salva con la propria unit.
   */
  const renderUnitCells = (athlete: Athlete, test: ITestDefinition, units: string[]) =>
    units.map((unit) => {
      // colId univoco per navigazione verticale Enter (sez.3.1)
      const colId = `${test.id}__unit__${unit}`;
      const cellKey = `${athlete.id}_${test.id}__unit__${unit}`;
      const isSaving = !!savingCells[cellKey];
      return (
        <td key={colId} className={styles.tdCenter}>
          <div className={styles.cellWrapper}>
            <input
              type="text"
              inputMode="decimal"
              data-athlete={athlete.id}
              data-col={colId}
              value={getUnitCellValue(athlete.id, test.id, unit)}
              onChange={(e) => handleUnitCellChange(athlete.id, test.id, unit, e.target.value)}
              onBlur={() => handleUnitCellBlur(athlete.id, test.id, unit)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // sez.3.1 — Enter naviga verso atleta sottostante, stesso test/unit
                  handleEnterNavigateDown(e, athlete.id, filteredAthletes, colId);
                }
              }}
              className={[styles.cellInput, isSaving ? styles.cellInputSaving : ''].filter(Boolean).join(' ')}
              disabled={isSaving}
            />
            {isSaving && <div className={styles.cellSpinner} />}
          </div>
        </td>
      );
    });

  /**
   * Cella multi-metrica con sotto-campi affiancati (spec sez.3.2).
   * Marker "incompleto" su sotto-campi vuoti (sez.5).
   */
  const renderMetricCell = (athlete: Athlete, test: ITestDefinition, metrics: string[]) => {
    const cellKey = `${athlete.id}_${test.id}`;
    const isSaving = !!savingCells[cellKey];
    // Verifica se almeno un sotto-campo ha un valore (per il marker sez.5)
    const hasAnyValue = metrics.some((m) => getMetricValue(athlete.id, test.id, m).trim() !== '');

    return (
      <td key={test.id} className={styles.tdCenter}>
        <div className={styles.cellWrapper}>
          <div className={styles.metricSubfields}>
            {metrics.map((m) => {
              const val = getMetricValue(athlete.id, test.id, m);
              // sez.5 — marker "campo mancante" se la cella è parzialmente compilata
              const isIncomplete = hasAnyValue && val.trim() === '';
              // colId per navigazione Enter verticale (sez.3.1)
              const colId = `${test.id}__metric__${m}`;
              return (
                <label key={m} className={styles.metricField}>
                  <span className={styles.metricLabel}>{m}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    data-cell={cellKey}
                    data-athlete={athlete.id}
                    data-col={colId}
                    value={val}
                    onChange={(e) => handleMetricChange(athlete.id, test.id, m, e.target.value)}
                    onBlur={(e) => handleMetricBlur(athlete.id, test, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // sez.3.1 — Enter naviga verso atleta sottostante, stessa metrica
                        handleEnterNavigateDown(e, athlete.id, filteredAthletes, colId);
                      }
                    }}
                    className={[
                      styles.cellInput,
                      styles.metricInput,
                      isSaving ? styles.cellInputSaving : '',
                      isIncomplete ? styles.cellInputIncomplete : '',
                    ].filter(Boolean).join(' ')}
                    disabled={isSaving}
                  />
                </label>
              );
            })}
          </div>
          {isSaving && <div className={styles.cellSpinner} />}
        </div>
      </td>
    );
  };

  /**
   * Cella single-value (comportamento esistente).
   */
  const renderSingleCell = (athlete: Athlete, test: ITestDefinition) => {
    const isSaving = !!savingCells[`${athlete.id}_${test.id}`];
    const lastResult = getLastResult(athlete.id, test.id, historyMap);
    const placeholderText = lastResult && lastResult.value != null
      ? `${lastResult.value.toFixed(
          test.defaultUnit === '%' ? 1 : test.defaultUnit === 's' ? 2 : 0
        )} ${getUnitLabel(test, test.defaultUnit)}`
      : '';
    // colId per navigazione Enter verticale (sez.3.1)
    const colId = test.id;

    return (
      <td key={test.id} className={styles.tdCenter}>
        <div className={styles.cellWrapper}>
          <input
            type="text"
            inputMode="decimal"
            data-athlete={athlete.id}
            data-col={colId}
            value={getCellValue(athlete.id, test.id)}
            onChange={(e) => handleCellChange(athlete.id, test.id, e.target.value)}
            onBlur={() => handleCellBlur(athlete.id, test.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // sez.3.1 — Enter naviga verso atleta sottostante, stesso test
                handleEnterNavigateDown(e, athlete.id, filteredAthletes, colId);
              }
            }}
            placeholder={placeholderText}
            className={[styles.cellInput, isSaving ? styles.cellInputSaving : ''].filter(Boolean).join(' ')}
            disabled={isSaving}
          />
          {isSaving && <div className={styles.cellSpinner} />}
        </div>
      </td>
    );
  };

  return (
    <div className={styles.tableCard}>
      <div className={styles.tableScrollWrapper}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.thSearchSticky}>
                <div className={styles.searchWrapper}>
                  <Search className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Cerca atleta..."
                    value={athleteSearch}
                    onChange={(e) => setAthleteSearch(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
              </th>
              {categoryTests.map((test: ITestDefinition) => renderTestHeader(test))}
            </tr>
          </thead>
          <tbody>
            {filteredAthletes.map((athlete) => (
              <tr key={athlete.id} className={styles.tableRow}>
                <td className={styles.tdNameSticky}>
                  <Link to={`/dashboard/users/${athlete.id}?tab=testing`} className={styles.athleteLink}>
                    {athlete.name}
                  </Link>
                </td>
                {categoryTests.map((test: ITestDefinition) => {
                  const units = getUnits(test);
                  // sez.3/6 — multi-unit: colonne affiancate per unità
                  if (units.length > 1) {
                    return renderUnitCells(athlete, test, units);
                  }

                  const metrics = getMetrics(test);
                  // sez.3.2 — multi-metrica: sotto-campi affiancati
                  if (metrics.length > 0) {
                    return renderMetricCell(athlete, test, metrics);
                  }

                  // Cella singola (comportamento esistente)
                  return renderSingleCell(athlete, test);
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
